import React, { useState, useEffect, useContext } from 'react';
import { Container, Row } from 'reactstrap';
import PracticeCard from './PracticeCard';
import Lottie from 'react-lottie';
import { default as animationData } from '../../assets/loader.json';
import { useLocation } from "react-router";
import { checkPremiumStatus } from '../../utils/auth';
import { UserContext } from '../UserContext';
import { cx, css } from '@emotion/css';
import * as assignmentStyles from '../assignments/assignments.style';

const pageBgStyles = css`
  background-color: #fdfbf7;
  background-image: 
    radial-gradient(at 100% 0%, rgba(241, 102, 2, 0.04) 0px, transparent 50%),
    radial-gradient(at 0% 100%, rgba(244, 203, 14, 0.02) 0px, transparent 50%);
  min-height: 100vh;
  padding-bottom: 100px;
  overflow-x: hidden;
`;

const headerStyles = {
  container: css`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 1rem;
    margin-bottom: 1.5rem;
    margin-top: 1.5rem;
    border-bottom: 1px solid rgba(241, 102, 2, 0.08);
    padding-bottom: 1.2rem;

    @media (max-width: 991px) {
      flex-direction: column;
      align-items: flex-start;
      gap: 1.2rem;
      margin-bottom: 1.5rem;
      padding-bottom: 1.2rem;
    }
  `,
  leftCol: css`
    flex: 1;
    max-width: 850px;
  `,
  topLabel: css`
    font-family: 'Inter', sans-serif;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.18em;
    color: rgba(241, 102, 2, 0.7);
    margin-bottom: 0.6rem;
    display: block;
  `,
  heading: css`
    font-family: 'Outfit', 'Inter', sans-serif;
    font-size: 2.4rem;
    font-weight: 800;
    font-style: italic;
    line-height: 1.05;
    margin: 0 0 0.5rem 0;
    letter-spacing: -0.02em;
    text-transform: uppercase;

    .orange-part {
      color: #f16602;
      margin-right: 0.6rem;
      display: inline-block;
    }

    .outline-part {
      color: transparent;
      -webkit-text-stroke: 1.5px rgba(241, 102, 2, 0.25);
      font-weight: 900;
      display: inline-block;
    }

    @media (max-width: 768px) {
      font-size: 1.8rem;
      
      .outline-part {
        -webkit-text-stroke: 1px rgba(241, 102, 2, 0.25);
      }
    }
  `,
  description: css`
    font-family: 'Inter', sans-serif;
    font-size: 1.05rem;
    color: #554a42;
    line-height: 1.6;
    margin: 0;
    max-width: 700px;
    font-weight: 400;

    @media (max-width: 768px) {
      font-size: 0.95rem;
    }
  `,
  spellbowllicense: css`
    font-family: 'Inter', sans-serif;
    font-size: 0.78rem;
    color: #8c7665;
    font-weight: 500;
    margin-top: 1rem;
    padding: 8px 16px;
    background-color: rgba(241, 102, 2, 0.05);
    border-radius: 8px;
    display: inline-block;
  `
};

function PracticeScreen(props) {
  const [events, set_events] = useState([]);
  const [loadedevents, set_loadedevents] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  
  let data1 = useLocation();

  function importAll(r) {
    let images = {};
    r.keys().map((item, index) => { images[item.replace('./', '')] = r(item); });
    return images;
  }

  const loadEvents = (isUserPremium = false) => {
    const category = data1.state["category"];
    const level = data1.state["level"];
    const premiumlevel = data1.state["premiumlevel"] || 0;
    
    setIsPremium(isUserPremium);

    const quizes = importAll(require.context("./resources/", true, /\.(json)$/));
    const keys = Object.keys(quizes);
    let serialnumber = 0;
    const data12 = [];
    let time = 120;

    if (category === "SpellBowl") {
      time = 10;
    }

    for (let i = 0; i < keys.length; i++) {
      if (keys[i].includes(category) && keys[i].includes('Grade' + level)) {
        const processQuiz = () => {
          serialnumber += 1;
          const filename = keys[i].replace(/^.*[\/]/, '');
          let name = filename.replace(/\.json/gi, '').trim();
          
          if (name === "Senior Word List 2025_filtered" || name === "Elementary Word List 2025_filtered" || name === "Junior Word List 2025_filtered") {
            name = "Foreign origin words";
          }
          
          const quizData = quizes[keys[i]];
          const questions = quizData.questions || quizData;

          const json_object = {
            completed: 0,
            eventtype: 'Practice',
            evenstartdate: '2020-0-31',
            levels: ['Grade' + level],
            category: category,
            totalquestions: questions.length,
            level: 'Grade' + level,
            name: name,
            eventdisplay: '',
            qualificationno: questions.length - 1,
            time: time,
            questions: questions,
            id: serialnumber
          };

          data12.push(json_object);
        };

        if (keys[i].includes("premium")) {
          try {
            const premiumlabel = keys[i].substring(keys[i].indexOf('premium'), keys[i].lastIndexOf('/'));
            const premiumnumber = parseInt(premiumlabel.split("premium")[1], 10);
            
            if (premiumnumber === 0 || (isUserPremium && premiumnumber <= premiumlevel)) {
              processQuiz();
            }
          } catch (e) {
            console.error('Error processing premium content:', e);
          }
        } else {
          processQuiz();
        }
      }
    }

    set_events(data12);
    set_loadedevents(true);
  };

  useEffect(() => {
    const checkAndLoad = async () => {
      try {
        const { isPremium: premiumStatus } = await checkPremiumStatus();
        loadEvents(premiumStatus);
      } catch (error) {
        console.error('Error checking premium status, loading free content', error);
        loadEvents(false);
      }
    };
    
    checkAndLoad();
    document.title = 'Practice | QuizWizard';
  }, []);

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: animationData,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice'
    }
  };

  const currentCategory = data1.state["category"] || 'Practice';
  let displayCategory = currentCategory;
  if (displayCategory === "MathBowl") {
    displayCategory = "Mathematics";
  }

  let eventCards = events.map(event => {
    return (
      <PracticeCard
        imgsrc={"../assets/" + event.name + ".png"}
        title={event.name}
        key={event.id}
        levels={event.levels}
        totalquestions={event.totalquestions}
        event={event}
        category={data1.state}
      />
    );
  });

  return (
    <div className={cx(pageBgStyles)}>
      <Container style={{ marginTop: '2.5rem' }}>
        {loadedevents ? (
          <div>
            {/* Curated Header Layout */}
            <div className={cx(headerStyles.container)}>
              <div className={cx(headerStyles.leftCol)}>
                <span className={cx(headerStyles.topLabel)}>PRACTICE SESSIONS</span>
                <h1 className={cx(headerStyles.heading)}>
                  <span className="orange-part">{displayCategory}</span>
                  <span className="outline-part">Practice</span>
                </h1>
                <p className={cx(headerStyles.description)}>
                  Configure your question range limits, randomize list shuffling, specify custom countdown timers, and build your speed in customized mock trials.
                </p>
                {currentCategory.includes("SpellBowl") && (
                  <div className={cx(headerStyles.spellbowllicense)}>
                    All Word Lists utilized herein have been provided by the Indiana Association of School Principals, Inc.
                  </div>
                )}
              </div>
            </div>

            <div className={cx(assignmentStyles.challengeGrid)} style={{ padding: '0 0 80px 0' }}>
              {eventCards}
            </div>
          </div>
        ) : (
          <div className="welcomecenter" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
            <Lottie options={defaultOptions} height={200} width={200} />
            <p className="mt-3" style={{ fontWeight: '600', color: '#f16602' }}>Loading practice experiences...</p>
          </div>
        )}
      </Container>
    </div>
  );
}

export default PracticeScreen;